// Territory arena skins (territory-visuals.md §14, backlog SK-1, SK-2, SK-7): ball.io's own 16
// procedural skins, plus "none" (the plain block, id ""). Every design here is ours, drawn in code
// by the client (arena/skins.ts); none reproduces a reference skin, character, food or pattern.
//
// A skin is three things, all derived from the life's palette colour (so land stays uniquely
// coloured even when two players wear the same skin):
// - `pattern`: an optional repeating tile on the land TOP (not the side band, not the trail),
//   anchored to world cell (0, 0), `tileW × tileH` whole cells, marks in the ink tone;
// - `head`: a decoration on the rotating rounded block (D-V1), drawn on its top face, facing the
//   heading (or the pointer for the own head when `facing` is 'pointer'); `level` > 1 rises above
//   the block (the sprout);
// - `trail`: the trail tint, the main colour or its light tone.
//
// Tones (`arenaSkinTones`): ink = the main colour at V × 0.8 when its HSV value V ≥ 55, else the
// main colour mixed 35 % toward white; light = main mixed 35 % toward white; deep (head marks) =
// V × 0.55 when V ≥ 55, else main mixed 60 % toward white.
//
// Ids are `t:<slug>` (the `t:` namespace is the arena's; Classic presets have no colon and custom
// skins are `c:<hash>`), at most 40 bytes (STR_MAX_BYTES.skinId). Skins are cosmetic only: the id
// lives in the life's identity record (PLAYERS), never in sim state that moves, captures or pays.
//
// Pure. Consumers: server (PLAY validation, bot picks), client (renderer, picker).

import { mixRgb, type Rgb, rgbChannels, scaleRgb } from '../colors.js';

/** Land pattern kinds (client/arena/skins.ts draws each). */
export type ArenaPatternKind =
  | 'dots'
  | 'stripes'
  | 'checks'
  | 'waves'
  | 'chevron'
  | 'scales'
  | 'bricks'
  | 'confetti'
  | 'stars'
  | 'tartan'
  | 'terrazzo'
  | 'circuit'
  | 'leaves'
  | 'flakes'
  | 'rings'
  | 'scanlines';

/** Head decoration kinds. */
export type ArenaHeadKind =
  | 'dots3'
  | 'band'
  | 'check'
  | 'wave'
  | 'chevron'
  | 'arcs'
  | 'mortar'
  | 'sprinkles'
  | 'star'
  | 'plus'
  | 'chips'
  | 'chip'
  | 'sprout'
  | 'flake'
  | 'eye'
  | 'visor';

export interface ArenaSkinPattern {
  readonly kind: ArenaPatternKind;
  /** Tile size in whole cells (the tile repeats on world cell multiples). */
  readonly tileW: number;
  readonly tileH: number;
}

export interface ArenaSkinHead {
  readonly kind: ArenaHeadKind;
  /** 'pointer': the own head's decoration faces the pointer target (others: the heading). */
  readonly facing: 'heading' | 'pointer';
  /** Decoration size relative to the block's top face (1 = the face; ≤ 1.3). */
  readonly scale: number;
  /** Height in block lifts: 1 = on the top face; > 1 rises above it. */
  readonly level: number;
  /** Pulses (scale 1 → 0.85 → 1 over `ARENA_SKIN_PULSE_MS`). */
  readonly pulse: boolean;
}

export interface ArenaSkin {
  /** `t:<slug>`. */
  readonly id: string;
  readonly slug: string;
  /** English display name (our own words; the client shows `arena.skin.<slug>` from i18n). */
  readonly name: string;
  readonly pattern: ArenaSkinPattern | null;
  readonly head: ArenaSkinHead;
  readonly trail: 'main' | 'light';
}

/** The `t:` namespace prefix. */
export const ARENA_SKIN_PREFIX = 't:';
/** One pulse period (head layers with `pulse`). */
export const ARENA_SKIN_PULSE_MS = 1000;
/** The largest head decoration scale. */
export const ARENA_SKIN_MAX_HEAD_SCALE = 1.3;

function skin(
  slug: string,
  name: string,
  pattern: readonly [ArenaPatternKind, number, number] | null,
  head: ArenaHeadKind,
  extra: {
    facing?: 'heading' | 'pointer';
    scale?: number;
    level?: number;
    pulse?: boolean;
    trail?: 'main' | 'light';
  } = {},
): ArenaSkin {
  return Object.freeze({
    id: ARENA_SKIN_PREFIX + slug,
    slug,
    name,
    pattern: pattern
      ? Object.freeze({ kind: pattern[0], tileW: pattern[1], tileH: pattern[2] })
      : null,
    head: Object.freeze({
      kind: head,
      facing: extra.facing ?? 'heading',
      scale: extra.scale ?? 1,
      level: extra.level ?? 1,
      pulse: extra.pulse ?? false,
    }),
    trail: extra.trail ?? 'main',
  });
}

/** The catalogue, in picker order (after "none"). */
export const ARENA_SKINS: readonly ArenaSkin[] = Object.freeze([
  skin('dots', 'Polka', ['dots', 5, 5], 'dots3'),
  skin('stripes', 'Candy Stripe', ['stripes', 4, 4], 'band'),
  skin('checks', 'Picnic', ['checks', 6, 6], 'check'),
  skin('waves', 'Tide', ['waves', 6, 3], 'wave'),
  skin('chevron', 'Arrowhead', ['chevron', 4, 4], 'chevron'),
  skin('scales', 'Scales', ['scales', 4, 2], 'arcs'),
  skin('bricks', 'Brickwork', ['bricks', 4, 2], 'mortar'),
  skin('confetti', 'Confetti', ['confetti', 8, 8], 'sprinkles'),
  skin('stars', 'Starry', ['stars', 7, 7], 'star', { pulse: true }),
  skin('tartan', 'Tartan', ['tartan', 6, 6], 'plus'),
  skin('terrazzo', 'Terrazzo', ['terrazzo', 7, 7], 'chips'),
  skin('circuit', 'Circuit', ['circuit', 6, 6], 'chip', { trail: 'light' }),
  skin('leaves', 'Sprout', ['leaves', 6, 6], 'sprout', { level: 1.3, scale: 1.3 }),
  skin('flakes', 'Frost', ['flakes', 7, 7], 'flake'),
  skin('peeper', 'Peeper', ['rings', 5, 5], 'eye', { facing: 'pointer' }),
  skin('visor', 'Visor', ['scanlines', 2, 2], 'visor', { trail: 'light' }),
]);

/** Every arena skin id, in catalogue order. */
export const ARENA_SKIN_IDS: readonly string[] = Object.freeze(ARENA_SKINS.map((s) => s.id));

const BY_ID: ReadonlyMap<string, ArenaSkin> = new Map(ARENA_SKINS.map((s) => [s.id, s]));

/** True for a catalogue id (`""`, "no skin", is not an id: use `normalizeArenaSkinId`). */
export function isArenaSkinId(id: string): boolean {
  return BY_ID.has(id);
}

/** A raw (client) skin id as the arena keeps it: a catalogue id, else `""` (no skin). */
export function normalizeArenaSkinId(raw: unknown): string {
  return typeof raw === 'string' && BY_ID.has(raw) ? raw : '';
}

/** The skin for an id, or null (`""`, unknown ids). */
export function arenaSkinById(id: string): ArenaSkin | null {
  return BY_ID.get(id) ?? null;
}

export interface ArenaSkinTones {
  /** Pattern marks on the land top. */
  readonly ink: Rgb;
  /** The light trail tint and secondary marks. */
  readonly light: Rgb;
  /** Head marks (stronger than ink: the head is small). */
  readonly deep: Rgb;
}

/** HSV value 0–100. */
function hsvValue(c: Rgb): number {
  const [r, g, b] = rgbChannels(c);
  return (Math.max(r, g, b) / 255) * 100;
}

/** Below this HSV value the ink is a light tint instead of a dark one. */
export const ARENA_SKIN_DARK_V = 55;

const toneCache = new Map<Rgb, ArenaSkinTones>();

/** The tones every skin mark is drawn in, from the life's palette colour (header). */
export function arenaSkinTones(main: Rgb): ArenaSkinTones {
  let t = toneCache.get(main);
  if (t) return t;
  const bright = hsvValue(main) >= ARENA_SKIN_DARK_V;
  const light = mixRgb(main, 0xffffff, 0.35);
  t = Object.freeze({
    ink: bright ? scaleRgb(main, 0.8) : light,
    light,
    deep: bright ? scaleRgb(main, 0.55) : mixRgb(main, 0xffffff, 0.6),
  });
  if (toneCache.size > 256) toneCache.clear();
  toneCache.set(main, t);
  return t;
}
